// src/utils/errorHandler.ts
import { FirebaseError } from 'firebase/app';

export interface ErrorLog {
  message: string;
  stack?: string;
  timestamp: Date;
  userId?: string;
  context?: Record<string, any>;
  severity: 'error' | 'warning' | 'info';
}

class ErrorHandler {
  private static instance: ErrorHandler;
  private errorQueue: ErrorLog[] = [];
  private maxQueueSize = 50;

  private constructor() {
    this.setupGlobalErrorHandlers();
    this.setupPeriodicFlush();
  }

  static getInstance(): ErrorHandler {
    if (!ErrorHandler.instance) {
      ErrorHandler.instance = new ErrorHandler();
    }
    return ErrorHandler.instance;
  }

  private setupGlobalErrorHandlers(): void {
    // Catch unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      this.logError(
        new Error(event.reason?.message || 'Unhandled promise rejection'),
        { context: 'unhandledRejection' }
      );
      event.preventDefault();
    });

    // Catch general errors
    window.addEventListener('error', (event) => {
      this.logError(event.error, { 
        context: 'globalError',
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno
      });
    });
  }

  private setupPeriodicFlush(): void {
    // Flush error queue every 30 seconds if there are errors
    setInterval(() => {
      if (this.errorQueue.length > 0) {
        this.flushErrors();
      }
    }, 30000);

    // Also flush on page unload
    window.addEventListener('beforeunload', () => {
      if (this.errorQueue.length > 0) {
        this.flushErrors();
      }
    });
  }

  logError(error: Error | FirebaseError | unknown, context?: Record<string, any>): void {
    const errorLog: ErrorLog = {
      message: this.getErrorMessage(error),
      stack: error instanceof Error ? error.stack : undefined,
      timestamp: new Date(),
      userId: this.getCurrentUserId(),
      context: {
        ...context,
        userAgent: navigator.userAgent,
        url: window.location.href,
        ...(error instanceof FirebaseError && { firebaseCode: error.code })
      },
      severity: 'error'
    };

  // console.error('Error logged:', errorLog);
    this.errorQueue.push(errorLog);

    // Flush immediately if queue is getting large
    if (this.errorQueue.length >= this.maxQueueSize) {
      this.flushErrors();
    }

    // Show user-friendly error message
    this.showUserNotification(error);
  }

  logWarning(message: string, context?: Record<string, any>): void {
    const warningLog: ErrorLog = {
      message,
      timestamp: new Date(),
      userId: this.getCurrentUserId(),
      context,
      severity: 'warning'
    };

  // console.warn('Warning logged:', warningLog);
    this.errorQueue.push(warningLog);
  }

  private getErrorMessage(error: Error | FirebaseError | unknown): string {
    if (error instanceof FirebaseError) {
      return this.getFirebaseErrorMessage(error);
    }
    if (error instanceof Error) {
      return error.message;
    }
    if (typeof error === 'string') {
      return error;
    }
    return 'An unexpected error occurred';
  }

  private getFirebaseErrorMessage(error: FirebaseError): string {
    const errorMessages: Record<string, string> = {
      'auth/user-not-found': 'No account found with this email address.',
      'auth/wrong-password': 'Incorrect password. Please try again.',
      'auth/invalid-email': 'Please enter a valid email address.',
      'auth/user-disabled': 'This account has been disabled. Please contact support.',
      'auth/email-already-in-use': 'An account with this email already exists.',
      'auth/weak-password': 'Password should be at least 6 characters.',
      'auth/network-request-failed': 'Network error. Please check your connection.',
      'auth/too-many-requests': 'Too many attempts. Please try again later.',
      'permission-denied': 'You do not have permission to perform this action.',
      'not-found': 'The requested resource was not found.',
      'deadline-exceeded': 'The operation timed out. Please try again.',
      'resource-exhausted': 'Quota exceeded. Please try again later.',
      'failed-precondition': 'Operation failed. Please refresh and try again.',
      'aborted': 'Operation was cancelled.',
      'out-of-range': 'Invalid input provided.',
      'unimplemented': 'This feature is not yet available.',
      'internal': 'An internal error occurred. Please try again.',
      'unavailable': 'Service temporarily unavailable. Please try again.',
      'data-loss': 'Data loss detected. Please contact support.',
      'unauthenticated': 'Please sign in to continue.'
    };

    return errorMessages[error.code] || error.message || 'An error occurred';
  }

  private showUserNotification(error: Error | FirebaseError | unknown): void {
    const message = this.getErrorMessage(error);
    
    // Dispatch custom event that the notification system can listen to
    window.dispatchEvent(new CustomEvent('app-error', {
      detail: { message, type: 'error' }
    }));
  }

  private getCurrentUserId(): string | undefined {
    // Get from your auth context or Firebase auth
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      return user?.uid;
    } catch {
      return undefined;
    }
  }

  private async flushErrors(): Promise<void> {
    if (this.errorQueue.length === 0) return;

    const errors = [...this.errorQueue];
    this.errorQueue = [];

    try {
      // Send to your logging service or Firebase Functions
      // For now, we'll store in localStorage for debugging
      const existingLogs = JSON.parse(localStorage.getItem('errorLogs') || '[]');
      const updatedLogs = [...existingLogs, ...errors].slice(-100); // Keep last 100
      localStorage.setItem('errorLogs', JSON.stringify(updatedLogs));

      // In production, send to your backend:
      // await fetch('/api/logs', {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify({ errors })
      // });
    } catch (e) {
  // console.error('Failed to flush errors:', e);
      // Re-add errors to queue if flush failed
      this.errorQueue = [...errors, ...this.errorQueue].slice(-this.maxQueueSize);
    }
  }

  // Method to wrap async operations with error handling
  async handleAsync<T>(
    operation: () => Promise<T>,
    context?: Record<string, any>
  ): Promise<T | null> {
    try {
      return await operation();
    } catch (error) {
      this.logError(error, context);
      return null;
    }
  }
}

export const errorHandler = ErrorHandler.getInstance();

// Helper function for wrapping components with error boundary
export function withErrorHandler<T extends (...args: any[]) => any>(
  fn: T,
  context?: Record<string, any>
): T {
  return ((...args: Parameters<T>) => {
    try {
      const result = fn(...args);
      if (result instanceof Promise) {
        return result.catch((error) => {
          errorHandler.logError(error, { ...context, args });
          throw error;
        });
      }
      return result;
    } catch (error) {
      errorHandler.logError(error, { ...context, args });
      throw error;
    }
  }) as T;
}